/**
 * AUTO-GENERATED by agent-contracts-runtime. DO NOT EDIT.
 *
 * Regenerate via: npx agent-runtime generate
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// dsl-task-request
// ---------------------------------------------------------------------------

export const DslTaskRequestSchema = z.object({
  task_id: z.string(),
  scope: z.string().optional(),
  context: z.string().optional(),
});

export type DslTaskRequest = z.infer<typeof DslTaskRequestSchema>;

// ---------------------------------------------------------------------------
// dsl-task-result
// ---------------------------------------------------------------------------

export const DslTaskResultSchema = z.object({
  changed_files: z.array(z.string()).optional(),
  validation_result: z.enum(["pass", "fail"]),
  lint_result: z.enum(["pass", "fail", "skipped"]).optional(),
  generate_result: z.enum(["pass", "fail", "skipped"]).optional(),
  render_result: z.enum(["pass", "fail", "skipped"]).optional(),
  drift_detected: z.boolean().optional(),
  cross_reference_check: z.enum(["pass", "fail", "skipped"]).optional(),
  score: z.number().optional(),
  score_dimensions: z.record(z.string(), z.unknown()).optional(),
  notes: z.string().optional(),
});

export type DslTaskResult = z.infer<typeof DslTaskResultSchema>;

// ---------------------------------------------------------------------------
// audit-result
// ---------------------------------------------------------------------------

export const AuditResultSchema = z.object({
  summary: z.string(),
  risk_level: z.enum(["low", "medium", "high", "critical"]),
  findings: z.array(z.object({
  id: z.string().optional(),
  severity: z.enum(["info", "warning", "error", "critical"]),
  category: z.string(),
  target: z.string().optional(),
  location: z.string().optional(),
  message: z.string(),
  recommendation: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
  evidence: z.array(z.object({
  kind: z.enum(["file", "command", "schema", "diff", "stdout", "stderr", "text"]),
  target: z.string().optional(),
  location: z.string().optional(),
  excerpt: z.string().optional(),
})).optional(),
  details: z.record(z.string(), z.unknown()).optional(),
})),
  recommended_actions: z.array(z.object({
  kind: z.enum(["run_command", "edit_file", "review", "confirm", "block", "ignore"]),
  title: z.string(),
  command: z.string().optional(),
  target: z.string().optional(),
  rationale: z.string().optional(),
})).optional(),
  metadata: z.object({
  tool: z.string().optional(),
  command: z.string().optional(),
  version: z.string().optional(),
  generated_at: z.string().optional(),
  adapter: z.string().optional(),
  model: z.string().optional(),
}).optional(),
});

export type AuditResult = z.infer<typeof AuditResultSchema>;

// ---------------------------------------------------------------------------
// dsl-audit-result
// ---------------------------------------------------------------------------

export const DslAuditResultSchema = z.object({
  audit_type: z.enum(["completeness", "semantic", "prompt", "extensions"]),
  total_dimensions: z.number().int(),
  pass_count: z.number().int(),
  miss_count: z.number().int(),
  partial_count: z.number().int().optional(),
  agents_reviewed: z.number().int().optional(),
  prompts_reviewed: z.number().int().optional(),
  completion_criteria_coverage: z.object({
  all_dimensions_inspected: z.boolean().optional(),
  gaps_classified: z.boolean().optional(),
  gate_analysis_complete: z.boolean().optional(),
  guardrail_enforcement_verified: z.boolean().optional(),
  scope_overlap_analyzed: z.boolean().optional(),
  x_property_misuse_checked: z.boolean().optional(),
  hallucinated_permissions_checked: z.boolean().optional(),
  extension_consumption_checked: z.boolean().optional(),
}),
  critical_gaps: z.array(z.object({
  dimension: z.string().optional(),
  agent: z.string().optional(),
  gap_type: z.enum(["template_gap", "data_gap", "dsl_gap"]).optional(),
  severity: z.enum(["critical", "warning", "info"]).optional(),
})).optional(),
  recommendations: z.array(z.object({
  priority: z.enum(["P0", "P1", "P2"]).optional(),
  description: z.string().optional(),
  fix_type: z.enum(["template_fix", "dsl_fix", "regeneration"]).optional(),
})).optional(),
});

export type DslAuditResult = z.infer<typeof DslAuditResultSchema>;

// ---------------------------------------------------------------------------
// Schema registry
// ---------------------------------------------------------------------------

export const handoffSchemas = {
  "dsl-task-request": DslTaskRequestSchema,
  "dsl-task-result": DslTaskResultSchema,
  "audit-result": AuditResultSchema,
  "dsl-audit-result": DslAuditResultSchema,
} as const;

export type HandoffTypeId = keyof typeof handoffSchemas;

// ---------------------------------------------------------------------------
// Handoff envelope
// ---------------------------------------------------------------------------

export interface HandoffEnvelope<T extends HandoffTypeId = HandoffTypeId> {
  readonly type: T;
  readonly version: number;
  readonly payload: z.infer<(typeof handoffSchemas)[T]>;
}

// ---------------------------------------------------------------------------
// Factory functions — type-safe constructors with runtime validation
// ---------------------------------------------------------------------------

export const handoffs = {
  dslTaskRequest(payload: DslTaskRequest): HandoffEnvelope<"dsl-task-request"> {
    return {
      type: "dsl-task-request" as const,
      version: 1,
      payload: DslTaskRequestSchema.parse(payload),
    };
  },
  dslTaskResult(payload: DslTaskResult): HandoffEnvelope<"dsl-task-result"> {
    return {
      type: "dsl-task-result" as const,
      version: 1,
      payload: DslTaskResultSchema.parse(payload),
    };
  },
  auditResult(payload: AuditResult): HandoffEnvelope<"audit-result"> {
    return {
      type: "audit-result" as const,
      version: 1,
      payload: AuditResultSchema.parse(payload),
    };
  },
  dslAuditResult(payload: DslAuditResult): HandoffEnvelope<"dsl-audit-result"> {
    return {
      type: "dsl-audit-result" as const,
      version: 1,
      payload: DslAuditResultSchema.parse(payload),
    };
  },
} as const;
